import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { API } from "../App";
import axios from "axios";
import { 
  Search, MessageCircle, ChevronDown, ChevronUp, 
  ArrowLeft, Send, Loader2, Bot, User, HelpCircle
} from "lucide-react";

const FAQ_CATEGORIES = [
  {
    title: "Getting Started",
    questions: [
      {
        q: "How does ExpatMatch work?",
        a: "ExpatMatch uses AI to analyze your lifestyle, finances, and preferences through a comprehensive questionnaire. Based on your responses, we recommend the top countries that match your needs, complete with a match score and key highlights."
      },
      {
        q: "How long does the questionnaire take?",
        a: "The questionnaire typically takes 5-10 minutes to complete. It covers personal information, family situation, health, languages, finances, work preferences, and climate preferences."
      },
      {
        q: "Is my data secure?",
        a: "Yes, absolutely. We use industry-standard encryption, never share your data with third parties, and you can request data deletion at any time."
      }
    ]
  },
  {
    title: "Guides & Pricing",
    questions: [
      {
        q: "What's included in each Personalized Relocation Guide?",
        a: "Each guide includes: detailed visa requirements with savings/income thresholds, cost of living breakdown, healthcare comparison (with Numbeo Health Index vs. USA), real estate laws for foreigners, banking guide, tax information, internet access details, relocation timeline with benchmarks, and personalized hobby/interest recommendations. Plus a 'Day in the Life' video to help you visualize your future."
      },
      {
        q: "How much does a guide cost?",
        a: "Our bundle pricing helps you save: $20 for 1 country guide, $30 for 2 guides (save $10), or $35 for 3 guides (save $25). Most successful expats compare 2-3 countries before deciding."
      },
      {
        q: "Can I get a refund?",
        a: "Yes, we offer refunds within 24 hours of purchase if you haven't viewed the full guide. Contact us through the contact page for refund requests."
      },
      {
        q: "How do I download my guide?",
        a: "After purchase, go to your Dashboard and find the guide. Click 'View' then use the 'Download Full Guide' button to save it as a text file."
      }
    ]
  },
  {
    title: "Country Recommendations",
    questions: [
      {
        q: "How accurate are the match scores?",
        a: "Our AI analyzes 50+ factors including cost of living, safety, healthcare, visa accessibility, climate, and more. Match scores reflect how well a country aligns with YOUR specific needs based on your questionnaire responses."
      },
      {
        q: "Can I compare multiple countries?",
        a: "Yes! Use our Country Comparison tool (available from the Results page) to compare up to 4 countries side-by-side across key metrics — completely free."
      },
      {
        q: "What if my preferred country isn't in the results?",
        a: "You can enable 'Include Specific Countries' in the questionnaire's Additional Options to ensure your preferred countries are included in the recommendations."
      },
      {
        q: "Why don't I see more European countries?",
        a: "Our AI prioritizes geographic diversity and budget fit. If you have a lower budget, you'll often see better-value destinations in Central America, South America, Southeast Asia, and other regions where your money goes further."
      }
    ]
  },
  {
    title: "Account & Payments",
    questions: [
      {
        q: "Do I need an account to use ExpatMatch?",
        a: "Yes, a free account is required to save your questionnaire responses, view recommendations, and purchase guides. Your data persists across sessions."
      },
      {
        q: "What payment methods do you accept?",
        a: "We accept all major credit and debit cards through our secure Stripe payment processor."
      },
      {
        q: "Can I retake the questionnaire?",
        a: "Absolutely! You can retake the questionnaire anytime to get updated recommendations based on changing preferences or circumstances."
      }
    ]
  }
];

const FAQPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedCategories, setExpandedCategories] = useState(["Getting Started"]);
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  const toggleCategory = (title) => {
    setExpandedCategories(prev => 
      prev.includes(title) 
        ? prev.filter(t => t !== title)
        : [...prev, title]
    );
  };

  const filteredFAQs = FAQ_CATEGORIES.map(category => ({
    ...category,
    questions: category.questions.filter(
      q => q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
           q.a.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  const handleChatSubmit = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || chatLoading) return;

    const userMessage = chatInput.trim();
    setChatInput("");
    setChatMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setChatLoading(true);

    try {
      const response = await axios.post(`${API}/support/chat`, {
        message: userMessage,
        session_id: sessionId,
        attempt_count: chatMessages.filter(m => m.role === "user").length + 1
      });

      setSessionId(response.data.session_id);
      setChatMessages(prev => [...prev, { 
        role: "assistant", 
        content: response.data.response,
        resolved: response.data.resolved
      }]);
    } catch (error) {
      setChatMessages(prev => [...prev, { 
        role: "assistant", 
        content: "I'm having trouble connecting. Please try again or use our contact form for assistance.",
        resolved: false
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-8 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-muted-foreground hover:text-primary mb-4 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </button>
          <h1 className="text-3xl sm:text-4xl font-bold text-primary mb-2" data-testid="faq-title">
            How Can We Help?
          </h1>
          <p className="text-muted-foreground">
            Search our FAQ or chat with our AI assistant
          </p>
        </div>

        {/* Search Bar */}
        <div className="relative mb-8">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search for answers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 h-14 text-lg bg-card border-primary/20"
            data-testid="faq-search"
          />
        </div>

        {/* AI Chat Button */}
        <div className="mb-8 bg-accent/10 rounded-xl p-6 border border-accent/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Bot className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-primary">Can't find what you're looking for?</h3>
                <p className="text-sm text-muted-foreground">Chat with our AI assistant for instant help</p>
              </div>
            </div>
            <Button
              onClick={() => setShowChat(!showChat)}
              className="btn-animated bg-primary"
              data-testid="toggle-chat-btn"
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              {showChat ? "Hide Chat" : "Start Chat"}
            </Button>
          </div>

          {/* Chat Interface */}
          {showChat && (
            <div className="mt-6 bg-card rounded-xl border border-primary/10 overflow-hidden">
              {/* Chat Messages */}
              <div className="h-80 overflow-y-auto p-4 space-y-4">
                {chatMessages.length === 0 && (
                  <div className="text-center text-muted-foreground py-8">
                    <HelpCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>Ask me anything about ExpatMatch!</p>
                    <p className="text-sm mt-1">I can help with questionnaire, reports, payments, and more.</p>
                  </div>
                )}
                {chatMessages.map((msg, i) => (
                  <div
                    key={"chat-faq-" + i}
                    className={`flex gap-3 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    {msg.role === "assistant" && (
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Bot className="h-4 w-4 text-primary" />
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] rounded-xl px-4 py-3 ${
                        msg.role === "user"
                          ? "bg-primary text-white"
                          : "bg-muted"
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                    {msg.role === "user" && (
                      <div className="w-8 h-8 rounded-full bg-secondary/20 flex items-center justify-center flex-shrink-0">
                        <User className="h-4 w-4 text-secondary" />
                      </div>
                    )}
                  </div>
                ))}
                {chatLoading && (
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <Loader2 className="h-4 w-4 text-primary animate-spin" />
                    </div>
                    <div className="bg-muted rounded-xl px-4 py-3">
                      <p className="text-sm text-muted-foreground">Thinking...</p>
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Chat Input */}
              <form onSubmit={handleChatSubmit} className="border-t border-primary/10 p-4 flex gap-2">
                <Input
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder="Type your question..."
                  className="flex-1"
                  data-testid="chat-input"
                />
                <Button 
                  type="submit" 
                  disabled={chatLoading || !chatInput.trim()}
                  className="bg-primary"
                  data-testid="chat-send-btn"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </form>
            </div>
          )}
        </div>

        {/* FAQ Categories */}
        <div className="space-y-4">
          {(searchQuery ? filteredFAQs : FAQ_CATEGORIES).map((category) => (
            <div
              key={category.title}
              className="bg-card rounded-xl border border-primary/10 overflow-hidden"
            >
              <button
                onClick={() => toggleCategory(category.title)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
                data-testid={`category-${category.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <h2 className="text-lg font-semibold text-primary">{category.title}</h2>
                {expandedCategories.includes(category.title) ? (
                  <ChevronUp className="h-5 w-5 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-muted-foreground" />
                )}
              </button>
              
              {expandedCategories.includes(category.title) && (
                <div className="px-6 pb-4 space-y-4">
                  {category.questions.map((item, i) => (
                    <div
                      key={"faq-" + category.title + "-" + i}
                      className="border-t border-primary/5 pt-4 first:border-0 first:pt-0"
                    >
                      <h3 className="font-medium text-foreground mb-2">{item.q}</h3>
                      <p className="text-muted-foreground text-sm">{item.a}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-12 text-center bg-card rounded-xl border border-primary/10 p-8">
          <h3 className="text-xl font-semibold text-primary mb-2">Still Need Help?</h3>
          <p className="text-muted-foreground mb-4">
            Our support team is here to assist you
          </p>
          <Button
            onClick={() => navigate("/contact")}
            variant="outline"
            data-testid="contact-cta-btn"
          >
            Contact Support
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
